'use client';

import { clientService, userService } from '@/@admin/services';
import { featureFlagService } from '@/@feature-flags/services';
import type { CreateOverrideRequest } from '@/@feature-flags/types/api';
import {
    FEATURE_FLAGS_QUERY_CONFIG,
    FEATURE_FLAGS_QUERY_KEYS,
} from '@/@feature-flags/types/constants';
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Radio,
    RadioGroup,
    Select,
    SelectItem,
    Switch,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

const SEARCH_LIMIT = 100;

type OverrideType = 'org' | 'user' | 'group';

interface AddOverrideModalProps {
    isOpen: boolean;
    onClose: () => void;
    flagKey: string;
    onSubmit: (key: string, payload: CreateOverrideRequest) => Promise<unknown>;
    isSubmitting: boolean;
}

export function AddOverrideModal({
    isOpen,
    onClose,
    flagKey,
    onSubmit,
    isSubmitting,
}: AddOverrideModalProps) {
    const [overrideType, setOverrideType] = useState<OverrideType>('org');
    const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch] = useDebounceValue(searchInput, 300);
    const [resetKey, setResetKey] = useState(0);

    const { data: clients = [], isFetching: isSearchingClients } = useQuery({
        queryKey: ['admin', 'ffOverrideClientSearch', debouncedSearch],
        queryFn: () =>
            clientService.getClients({ page: 1, limit: SEARCH_LIMIT, searchTerm: debouncedSearch }),
        enabled: overrideType === 'org' && isOpen && debouncedSearch.length >= 2,
    });

    const { data: users = [], isFetching: isSearchingUsers } = useQuery({
        queryKey: ['admin', 'ffOverrideUserSearch', debouncedSearch],
        queryFn: () =>
            userService.getUsers({ page: 1, limit: SEARCH_LIMIT, searchTerm: debouncedSearch }),
        enabled: overrideType === 'user' && isOpen && debouncedSearch.length >= 2,
    });

    const { data: groups = [], isLoading: isLoadingGroups } = useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEYS.groups,
        queryFn: () => featureFlagService.listGroups(),
        staleTime: FEATURE_FLAGS_QUERY_CONFIG.STALE_TIME,
        gcTime: FEATURE_FLAGS_QUERY_CONFIG.GC_TIME,
        enabled: isOpen,
    });

    useEffect(() => {
        if (isOpen) {
            setSelectedTargetId(null);
            setSelectedGroup(null);
            setEnabled(true);
            setSearchInput('');
            setResetKey((k) => k + 1);
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const isValid = overrideType === 'group' ? selectedGroup != null : selectedTargetId != null;

    const handleSubmit = useCallback(async () => {
        if (!isValid) return;

        const payload: CreateOverrideRequest = { enabled };
        if (overrideType === 'org') {
            payload.orgId = selectedTargetId!;
        } else if (overrideType === 'user') {
            payload.orgUserId = selectedTargetId!;
        } else {
            payload.cognitoGroup = selectedGroup!;
        }

        await onSubmit(flagKey, payload);
        handleClose();
    }, [
        isValid,
        enabled,
        overrideType,
        selectedTargetId,
        selectedGroup,
        flagKey,
        onSubmit,
        handleClose,
    ]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg" backdrop="blur">
            <ModalContent>
                <ModalHeader className="flex items-center gap-2">
                    <Icon icon="lucide:plus-circle" className="h-5 w-5 text-primary" />
                    <span>
                        Add Override for <code className="text-primary">{flagKey}</code>
                    </span>
                </ModalHeader>
                <ModalBody className="gap-4">
                    <RadioGroup
                        label="Override Type"
                        orientation="horizontal"
                        value={overrideType}
                        onValueChange={(v) => {
                            setOverrideType(v as OverrideType);
                            setSelectedTargetId(null);
                            setSelectedGroup(null);
                            setSearchInput('');
                            setResetKey((k) => k + 1);
                        }}
                    >
                        <Radio value="org">Organization</Radio>
                        <Radio value="user">User</Radio>
                        <Radio value="group">Group</Radio>
                    </RadioGroup>

                    {overrideType === 'user' && (
                        <Autocomplete
                            key={`user-search-${resetKey}`}
                            label="Search User"
                            placeholder="Type at least 2 characters to search by name or email..."
                            description={
                                debouncedSearch.length >= 2 && users.length === SEARCH_LIMIT
                                    ? 'Showing first results — refine your search for more'
                                    : undefined
                            }
                            isLoading={isSearchingUsers}
                            onInputChange={setSearchInput}
                            onSelectionChange={(key) => {
                                setSelectedTargetId(key != null ? Number(key) : null);
                            }}
                            isRequired
                        >
                            {users.map((u) => (
                                <AutocompleteItem key={u.org_user_id} textValue={u.user_email}>
                                    <div className="flex flex-col">
                                        <span className="text-small">
                                            {u.user_first_name} {u.user_last_name}
                                        </span>
                                        <span className="text-tiny text-default-400">
                                            {u.user_email} &middot; {u.org_name} (ID:{' '}
                                            {u.org_user_id})
                                        </span>
                                    </div>
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                    )}

                    {overrideType === 'org' && (
                        <Autocomplete
                            key={`client-search-${resetKey}`}
                            label="Search Organization"
                            placeholder="Type at least 2 characters to search by name..."
                            description={
                                debouncedSearch.length >= 2 && clients.length === SEARCH_LIMIT
                                    ? 'Showing first results — refine your search for more'
                                    : undefined
                            }
                            isLoading={isSearchingClients}
                            onInputChange={setSearchInput}
                            onSelectionChange={(key) => {
                                setSelectedTargetId(key != null ? Number(key) : null);
                            }}
                            isRequired
                        >
                            {clients.map((c) => (
                                <AutocompleteItem key={c.org_id} textValue={c.org_name}>
                                    <div className="flex flex-col">
                                        <span className="text-small">{c.org_name}</span>
                                        <span className="text-tiny text-default-400">
                                            ID: {c.org_id} &middot; SF: {c.sf_account_id}
                                        </span>
                                    </div>
                                </AutocompleteItem>
                            ))}
                        </Autocomplete>
                    )}

                    {overrideType === 'group' && (
                        <Select
                            key={`group-select-${resetKey}`}
                            label="Cognito Group"
                            placeholder="Select a group"
                            isLoading={isLoadingGroups}
                            isRequired
                            onSelectionChange={(keys) => {
                                const key = Array.from(keys)[0] as string | undefined;
                                setSelectedGroup(key ?? null);
                            }}
                        >
                            {groups.map((group) => (
                                <SelectItem key={group}>{group}</SelectItem>
                            ))}
                        </Select>
                    )}

                    <Switch isSelected={enabled} onValueChange={setEnabled} size="sm">
                        <span className="text-small">
                            {enabled ? 'Grant access' : 'Deny access'}
                        </span>
                    </Switch>
                </ModalBody>
                <ModalFooter>
                    <Button variant="flat" onPress={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onPress={handleSubmit}
                        isLoading={isSubmitting}
                        isDisabled={!isValid}
                    >
                        Add Override
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
